import { useForm } from "@mantine/form";
import {
  Modal,
  Title,
  Paper,
  Group,
  Button,
  Stack,
  Text,
  Badge,
  LoadingOverlay,
  Grid,
  Radio,
  Divider,
  Alert,
  Anchor,
  Box,
  ScrollArea,
} from "@mantine/core";
import { RichTextEditor } from "@mantine/tiptap";
import { useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Placeholder from "@tiptap/extension-placeholder";
import Underline from "@tiptap/extension-underline";
import {
  IconScale,
  IconDeviceFloppy,
  IconCheck,
  IconFileDescription,
  IconAlertCircle,
  IconAlertTriangle,
} from "@tabler/icons-react";
import { notifications } from "@mantine/notifications";
import { useEffect, useRef, useState } from "react";
import {
  useQaReviewDetail,
  useUpdateQaReview,
  useCompleteQaReview,
} from "../../hooks/useWorkflows";
import {
  QaAgreementLevel,
  QaAction,
  QaReviewCompleteData,
} from "../../services/workflows.service";
import {
  required,
  scrollToFirstError,
  showValidationNotification,
} from "../../utils/formValidation";
import { caseImagesService } from "../../services/caseImages.service";

// Agreement level options with descriptions
const AGREEMENT_LEVELS: {
  value: QaAgreementLevel;
  label: string;
  description: string;
}[] = [
  {
    value: "complete",
    label: "Yes - Complete agreement",
    description: "Diagnoses are identical in meaning and substance",
  },
  {
    value: "minor_variation",
    label: "Yes - Same diagnosis, minor wording differences",
    description: "Same conclusion with slightly different phrasing",
  },
  {
    value: "partial",
    label: "Partial - Related but different conclusions",
    description: "Similar findings but different diagnostic interpretation",
  },
  {
    value: "discrepancy",
    label: "No - Different diagnoses (same category: both benign)",
    description: "Different diagnoses but both within same severity category",
  },
  {
    value: "major_discrepancy",
    label: "No - Significant discrepancy (benign vs malignant)",
    description: "Clinically significant difference requiring action",
  },
];

// Recommended action options
const ACTION_OPTIONS: {
  value: QaAction;
  label: string;
  description: string;
}[] = [
  {
    value: "none",
    label: "None - Agreement confirmed",
    description: "No further action required",
  },
  {
    value: "notify_pathologist",
    label: "Notify original pathologist",
    description: "Alert the pathologist of the comparison results",
  },
  {
    value: "internal_discussion",
    label: "Flag for Internal Discussion",
    description: "Schedule case for internal review meeting",
  },
  {
    value: "amendment_needed",
    label: "Amendment may be needed",
    description: "Case may require report amendment",
  },
];

interface FormValues {
  agreementLevel: QaAgreementLevel | "";
  recommendedAction: QaAction | "";
}

interface QaReviewModalProps {
  opened: boolean;
  onClose: () => void;
  qaReviewId: string | null;
}

export function QaReviewModal({
  opened,
  onClose,
  qaReviewId,
}: QaReviewModalProps) {
  const {
    data: reviewData,
    isLoading,
    error,
  } = useQaReviewDetail(qaReviewId || undefined);

  const updateMutation = useUpdateQaReview();
  const completeMutation = useCompleteQaReview();

  const form = useForm<FormValues>({
    initialValues: {
      agreementLevel: "",
      recommendedAction: "",
    },
    validate: {
      agreementLevel: required("Please select an agreement assessment"),
      recommendedAction: required("Please select a recommended action"),
    },
  });

  const isCompleted = reviewData?.qaReview?.status === "completed";

  const isUpdatingEditorRef = useRef(false);

  const commentsEditor = useEditor({
    extensions: [
      StarterKit.configure({ heading: false }),
      Placeholder.configure({
        placeholder: "Enter any notes about the comparison...",
      }),
      Underline,
    ],
  });

  // Reset form when modal opens with new data
  useEffect(() => {
    if (opened && reviewData?.qaReview) {
      form.setValues({
        agreementLevel: reviewData.qaReview.agreementLevel || "",
        recommendedAction: reviewData.qaReview.recommendedAction || "",
      });
      if (commentsEditor) {
        isUpdatingEditorRef.current = true;
        commentsEditor.commands.setContent(reviewData.qaReview.comments || "");
        isUpdatingEditorRef.current = false;
      }
    } else if (!opened) {
      form.reset();
      if (commentsEditor) {
        isUpdatingEditorRef.current = true;
        commentsEditor.commands.setContent("");
        isUpdatingEditorRef.current = false;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened, reviewData, commentsEditor]);

  // Sync editor editability with completion status
  useEffect(() => {
    if (commentsEditor) {
      commentsEditor.setEditable(!isCompleted);
    }
  }, [isCompleted, commentsEditor]);

  // Auto-select "internal_discussion" when major_discrepancy is chosen
  useEffect(() => {
    if (form.values.agreementLevel === "major_discrepancy") {
      // Only auto-select if no action is currently selected (don't override user choice)
      if (!form.values.recommendedAction) {
        form.setFieldValue("recommendedAction", "internal_discussion");
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.values.agreementLevel]);

  const getCommentsHtml = (): string | undefined => {
    const html = commentsEditor?.getHTML() || "";
    return html.replace(/<[^>]*>/g, "").trim() ? html : undefined;
  };

  const handleSaveDraft = async () => {
    if (!qaReviewId) return;

    try {
      await updateMutation.mutateAsync({
        qaReviewId,
        data: {
          agreementLevel: form.values.agreementLevel || undefined,
          recommendedAction: form.values.recommendedAction || undefined,
          comments: getCommentsHtml(),
        },
      });
      notifications.show({
        title: "Draft Saved",
        message: "QA review draft has been saved",
        color: "green",
      });
    } catch (err) {
      notifications.show({
        title: "Error",
        message: "Failed to save draft",
        color: "red",
      });
    }
  };

  // Get action-specific success message based on recommended action
  const getActionSuccessMessage = (action: QaAction): string => {
    switch (action) {
      case "none":
        return "QA Review completed. No further action required.";
      case "notify_pathologist":
        return "QA Review completed. Original pathologist has been notified via case comment.";
      case "internal_discussion":
        return "QA Review completed. Case has been flagged for internal discussion.";
      case "amendment_needed":
        return "QA Review completed. Case flagged for potential amendment and internal discussion created.";
      default:
        return "The QA review has been successfully completed.";
    }
  };

  const handleComplete = form.onSubmit(
    async (values) => {
      if (!qaReviewId) return;

      try {
        const data: QaReviewCompleteData = {
          agreementLevel: values.agreementLevel as QaAgreementLevel,
          recommendedAction: values.recommendedAction as QaAction,
          comments: getCommentsHtml(),
        };

        await completeMutation.mutateAsync({ qaReviewId, data });
        notifications.show({
          title: "QA Review Completed",
          message: getActionSuccessMessage(
            values.recommendedAction as QaAction
          ),
          color: "green",
        });
        onClose();
      } catch (err) {
        showValidationNotification("Failed to complete QA review");
      }
    },
    () => scrollToFirstError(form)
  );

  const [isLoadingExternalReport, setIsLoadingExternalReport] = useState(false);

  const handleViewExternalReport = async () => {
    if (!reviewData?.externalReportFileKey) return;
    const fileName = `external-report-${reviewData.caseNumber}.pdf`;

    setIsLoadingExternalReport(true);
    try {
      const result = await caseImagesService.getDownloadUrl(
        reviewData.externalReportFileKey,
        fileName
      );
      window.open(result.url, "_blank");
    } catch (err) {
      notifications.show({
        title: "Error",
        message: "Failed to load external report",
        color: "red",
      });
    } finally {
      setIsLoadingExternalReport(false);
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      closeOnClickOutside={false}
      size="xl"
      title={
        <Group gap="xs">
          <IconScale size={24} color="var(--mantine-color-blue-6)" />
          <Title order={3}>
            Quality Assurance - External Opinion Comparison
          </Title>
          {isCompleted && (
            <Badge color="green" size="lg">
              Completed
            </Badge>
          )}
        </Group>
      }
      styles={{
        body: {
          display: "flex",
          flexDirection: "column",
          height: "calc(100vh - 180px)",
          padding: 0,
          overflowX: "hidden",
        },
      }}
    >
      <LoadingOverlay visible={isLoading} />

      {error && (
        <Alert color="red" icon={<IconAlertCircle size={16} />} m="md">
          Failed to load QA review: {error.message}
        </Alert>
      )}

      {reviewData && (
        <form
          onSubmit={handleComplete}
          style={{ display: "flex", flexDirection: "column", height: "100%" }}
        >
          {/* Scrollable Content */}
          <ScrollArea style={{ flex: 1 }} p="md" type="auto" offsetScrollbars>
            {/* Case Info Header */}
            <Paper p="md" mb="md" withBorder>
              <Grid>
                <Grid.Col span={4}>
                  <Text size="sm" c="dimmed">
                    Case
                  </Text>
                  <Text fw={500}>{reviewData.caseNumber}</Text>
                </Grid.Col>
                <Grid.Col span={4}>
                  <Text size="sm" c="dimmed">
                    External Institution
                  </Text>
                  <Text fw={500}>{reviewData.institutionName}</Text>
                </Grid.Col>
                <Grid.Col span={4}>
                  <Text size="sm" c="dimmed">
                    Patient
                  </Text>
                  <Text fw={500}>
                    {reviewData.patient.lastName},{" "}
                    {reviewData.patient.firstName}
                  </Text>
                </Grid.Col>
                <Grid.Col span={4}>
                  <Text size="sm" c="dimmed">
                    Original Pathologist
                  </Text>
                  <Text fw={500}>{reviewData.originalPathologistName}</Text>
                </Grid.Col>
                <Grid.Col span={4}>
                  <Text size="sm" c="dimmed">
                    QA Reviewer
                  </Text>
                  <Text fw={500}>{reviewData.reviewerName}</Text>
                </Grid.Col>
                {reviewData.externalReportDate && (
                  <Grid.Col span={4}>
                    <Text size="sm" c="dimmed">
                      External Report Date
                    </Text>
                    <Text fw={500}>
                      {new Date(
                        reviewData.externalReportDate
                      ).toLocaleDateString()}
                    </Text>
                  </Grid.Col>
                )}
              </Grid>
            </Paper>

            {/* Side-by-side diagnosis comparison */}
            <Grid mb="md">
              <Grid.Col span={6}>
                <Paper p="md" h="100%" withBorder>
                  <Group gap="xs" mb="md">
                    <IconFileDescription
                      size={20}
                      color="var(--mantine-color-blue-6)"
                    />
                    <Title order={5}>GCP Diagnosis</Title>
                  </Group>
                  <Divider mb="md" />
                  <Box>
                    {reviewData.gcpDiagnoses.length > 0 ? (
                      <Stack gap="lg">
                        {reviewData.gcpDiagnoses.map((specimen, idx) => (
                          <Box key={specimen.specimenId || idx}>
                            {/* Specimen Header */}
                            <Text size="sm" fw={700} mb="xs">
                              {specimen.specimenNumber} - {specimen.siteName}
                            </Text>

                            {/* Diagnosis Text */}
                            {specimen.diagnosisText &&
                              specimen.diagnosisText !== "<p></p>" && (
                                <Box
                                  mb="sm"
                                  p="sm"
                                  style={{
                                    backgroundColor:
                                      "var(--mantine-color-gray-1)",
                                    borderRadius: "var(--mantine-radius-sm)",
                                  }}
                                >
                                  <Text
                                    size="sm"
                                    style={{ whiteSpace: "pre-wrap" }}
                                    dangerouslySetInnerHTML={{
                                      __html: specimen.diagnosisText,
                                    }}
                                  />
                                </Box>
                              )}

                            {/* Blocks and Slides */}
                            {(specimen.blocks?.length ?? 0) > 0 && (
                              <Box pl="md">
                                <Text size="xs" c="dimmed" fw={500} mb="xs">
                                  Blocks & Slides:
                                </Text>
                                <Stack gap="xs">
                                  {specimen.blocks.map((block) => (
                                    <Box key={block.id} pl="sm">
                                      <Text size="xs" fw={600}>
                                        Block {block.number}
                                      </Text>
                                      {(block.slides?.length ?? 0) > 0 ? (
                                        <Group gap="xs" mt={4} wrap="wrap">
                                          {block.slides.map((slide) => (
                                            <Badge
                                              key={slide.id}
                                              size="xs"
                                              variant="light"
                                              color="blue"
                                            >
                                              {slide.stainCode ||
                                                `Slide ${slide.number}`}
                                            </Badge>
                                          ))}
                                        </Group>
                                      ) : (
                                        <Text size="xs" c="dimmed" fs="italic">
                                          No slides
                                        </Text>
                                      )}
                                    </Box>
                                  ))}
                                </Stack>
                              </Box>
                            )}
                          </Box>
                        ))}
                      </Stack>
                    ) : (
                      <Text c="dimmed" fs="italic">
                        No specimens found
                      </Text>
                    )}
                  </Box>
                  <Divider my="md" />
                  <Anchor
                    size="sm"
                    href={`/cases/${reviewData.qaReview.caseId}`}
                    target="_blank"
                  >
                    View Full GCP Report
                  </Anchor>
                </Paper>
              </Grid.Col>

              <Grid.Col span={6}>
                <Paper p="md" h="100%" withBorder>
                  <Group gap="xs" mb="md">
                    <IconFileDescription
                      size={20}
                      color="var(--mantine-color-orange-6)"
                    />
                    <Title order={5}>
                      {reviewData.institutionName} Diagnosis
                    </Title>
                  </Group>
                  <Divider mb="md" />
                  <ScrollArea.Autosize mah={200}>
                    {reviewData.externalDiagnosis ? (
                      <Text
                        size="sm"
                        style={{ whiteSpace: "pre-wrap" }}
                        dangerouslySetInnerHTML={{
                          __html: reviewData.externalDiagnosis,
                        }}
                      />
                    ) : (
                      <Text c="dimmed" fs="italic">
                        No external diagnosis recorded
                      </Text>
                    )}
                  </ScrollArea.Autosize>
                  <Divider my="md" />
                  {reviewData.externalReportFileKey ? (
                    <Anchor
                      size="sm"
                      component="button"
                      onClick={handleViewExternalReport}
                      style={{
                        cursor: isLoadingExternalReport ? "wait" : "pointer",
                      }}
                    >
                      {isLoadingExternalReport
                        ? "Loading..."
                        : `View external-report-${reviewData.caseNumber}.pdf`}
                    </Anchor>
                  ) : (
                    <Text size="sm" c="dimmed">
                      No external report file attached
                    </Text>
                  )}
                </Paper>
              </Grid.Col>
            </Grid>

            {/* QA Form - Agreement & Action side by side, Notes full width at bottom */}
            <Grid mb="md">
              {/* LEFT COLUMN: Agreement Assessment */}
              <Grid.Col span={6}>
                <Paper p="md" withBorder h="100%">
                  <Title order={5} mb="md">
                    Agreement Assessment{" "}
                    <Text span c="red" size="sm">
                      *
                    </Text>
                  </Title>
                  <Text size="sm" c="dimmed" mb="md">
                    Do the diagnoses agree?
                  </Text>

                  <Radio.Group
                    {...form.getInputProps("agreementLevel")}
                    error={form.errors.agreementLevel}
                  >
                    <Stack gap="xs">
                      {AGREEMENT_LEVELS.map((level) => (
                        <Radio
                          key={level.value}
                          value={level.value}
                          label={level.label}
                          description={level.description}
                          disabled={isCompleted}
                          size="sm"
                        />
                      ))}
                    </Stack>
                  </Radio.Group>
                </Paper>
              </Grid.Col>

              {/* RIGHT COLUMN: Action Required */}
              <Grid.Col span={6}>
                <Paper p="md" withBorder h="100%">
                  <Title order={5} mb="md">
                    Action Required{" "}
                    <Text span c="red" size="sm">
                      *
                    </Text>
                  </Title>
                  <Text size="sm" c="dimmed" mb="md">
                    What action should be taken?
                  </Text>

                  <Radio.Group
                    {...form.getInputProps("recommendedAction")}
                    error={form.errors.recommendedAction}
                  >
                    <Stack gap="xs">
                      {ACTION_OPTIONS.map((action) => (
                        <Radio
                          key={action.value}
                          value={action.value}
                          label={action.label}
                          description={action.description}
                          disabled={isCompleted}
                          size="sm"
                        />
                      ))}
                    </Stack>
                  </Radio.Group>
                </Paper>
              </Grid.Col>
            </Grid>

            {/* Discrepancy Warning Alert - shows when discrepancy or major_discrepancy is selected */}
            {(form.values.agreementLevel === "discrepancy" ||
              form.values.agreementLevel === "major_discrepancy") && (
              <Alert
                color={
                  form.values.agreementLevel === "major_discrepancy"
                    ? "red"
                    : "orange"
                }
                icon={<IconAlertTriangle size={16} />}
                mb="md"
                title={
                  form.values.agreementLevel === "major_discrepancy"
                    ? "Significant Discrepancy Detected"
                    : "Discrepancy Detected"
                }
              >
                {form.values.agreementLevel === "major_discrepancy" ? (
                  <Text size="sm">
                    A significant discrepancy (benign vs malignant) has been
                    identified. This requires immediate attention. It is
                    recommended to initiate an internal discussion or flag for
                    amendment review.
                  </Text>
                ) : (
                  <Text size="sm">
                    A discrepancy has been identified between the diagnoses.
                    Consider notifying the original pathologist or initiating an
                    internal discussion for peer review.
                  </Text>
                )}
              </Alert>
            )}

            {/* Full width: Reviewer Notes at bottom */}
            <Paper p="md" withBorder>
              <Title order={5} mb="md">
                Reviewer Notes
              </Title>
              <RichTextEditor editor={commentsEditor}>
                <RichTextEditor.Toolbar>
                  <RichTextEditor.ControlsGroup>
                    <RichTextEditor.Bold />
                    <RichTextEditor.Italic />
                    <RichTextEditor.Underline />
                  </RichTextEditor.ControlsGroup>
                  <RichTextEditor.ControlsGroup>
                    <RichTextEditor.BulletList />
                    <RichTextEditor.OrderedList />
                  </RichTextEditor.ControlsGroup>
                </RichTextEditor.Toolbar>
                <RichTextEditor.Content style={{ minHeight: 120 }} />
              </RichTextEditor>
            </Paper>

            {/* Completed Alert */}
            {isCompleted &&
              (reviewData?.qaReview.completedAt ||
                reviewData?.qaReview.actionTakenAt) && (
                <Alert color="green" icon={<IconCheck size={16} />} mt="md">
                  This QA review was completed on{" "}
                  {new Date(
                    reviewData.qaReview.completedAt ||
                      reviewData.qaReview.actionTakenAt!
                  ).toLocaleDateString()}{" "}
                  at{" "}
                  {new Date(
                    reviewData.qaReview.completedAt ||
                      reviewData.qaReview.actionTakenAt!
                  ).toLocaleTimeString()}
                  {reviewData?.qaReview.reviewedAt && (
                    <Text size="sm" mt="xs">
                      Review started:{" "}
                      {new Date(
                        reviewData.qaReview.reviewedAt
                      ).toLocaleDateString()}{" "}
                      at{" "}
                      {new Date(
                        reviewData.qaReview.reviewedAt
                      ).toLocaleTimeString()}
                    </Text>
                  )}
                </Alert>
              )}
          </ScrollArea>

          {/* Fixed Footer */}
          {!isCompleted && (
            <Box
              p="md"
              style={{
                borderTop: "1px solid var(--mantine-color-gray-3)",
                backgroundColor: "var(--mantine-color-body)",
              }}
            >
              <Group justify="space-between">
                <Button variant="default" onClick={onClose}>
                  Cancel
                </Button>
                <Group gap="xs">
                  <Button
                    variant="outline"
                    leftSection={<IconDeviceFloppy size={16} />}
                    onClick={handleSaveDraft}
                    loading={updateMutation.isPending}
                  >
                    Save Draft
                  </Button>
                  <Button
                    type="submit"
                    leftSection={<IconCheck size={16} />}
                    loading={completeMutation.isPending}
                  >
                    Complete QA Review
                  </Button>
                </Group>
              </Group>
            </Box>
          )}
        </form>
      )}
    </Modal>
  );
}

// Keep default export for compatibility
export default function QaReviewDetailPage() {
  return null;
}
